import { Controller } from "stimulus";

// Call Logs page: appends the next page of rows when the sentinel scrolls into view.
export default class extends Controller {
  static targets = ["body", "sentinel"];
  static values = { url: String, page: Number };

  declare readonly bodyTarget: HTMLElement;
  declare readonly sentinelTarget: HTMLElement;
  declare readonly hasSentinelTarget: boolean;
  declare urlValue: string;
  declare pageValue: number;

  private observer?: IntersectionObserver;
  private loading = false;

  connect(): void {
    if (!this.hasSentinelTarget) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.loadMore();
    }, { rootMargin: "200px" });
    this.observer.observe(this.sentinelTarget);
  }

  disconnect(): void {
    this.observer?.disconnect();
  }

  private nextPage(): number | null {
    const marker = this.bodyTarget.querySelector<HTMLElement>("[data-call-logs-next]");
    return marker ? Number(marker.dataset.callLogsNext) : null;
  }

  private async loadMore(): Promise<void> {
    const page = this.nextPage();
    if (this.loading || !page) return;
    this.loading = true;
    this.sentinelTarget.textContent = "Loading…";
    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("page", String(page));
      const res = await fetch(url.toString(), { headers: { Accept: "text/html" }, credentials: "same-origin" });
      if (!res.ok) throw new Error(String(res.status));
      this.bodyTarget.querySelector("[data-call-logs-next]")?.remove();
      this.bodyTarget.insertAdjacentHTML("beforeend", await res.text());
      this.pageValue = page;
    } catch {
      this.sentinelTarget.textContent = "Couldn't load more logs";
      return;
    } finally {
      this.loading = false;
    }
    this.sentinelTarget.textContent = "";
    if (!this.nextPage()) this.observer?.disconnect();
    else if (this.sentinelTarget.getBoundingClientRect().top < window.innerHeight) this.loadMore();
  }
}
